import { useCallback } from 'react';
import { message } from 'antd';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
    GROUP_PADDING_X,
    GROUP_PADDING_BOTTOM,
    GROUP_HEADER_HEIGHT,
    GROUP_MIN_WIDTH,
    GROUP_MIN_HEIGHT,
} from '../constants/canvas';
import {
    createNodeId,
} from '../utils/common';
import {
    getNodeAbsolutePosition,
    getNodeSize,
    validateGroupFlowStructure,
} from '../utils/graph';
import {
    getNodeResultImageItems,
    getNodeSourceImageItems,
} from '../utils/execution';
import { getDownloadFileName, getExtensionFromBlob } from '../utils/download';

/**
 * 画布分组操作与批量下载管理
 */
export const useCanvasGroup = ({
    nodesRef,
    edgesRef,
    selectedNodeIds,
    activeGroupId,
    setActiveGroupId,
    setNodes,
    setNextGroupNodeNumber,
    nextGroupNodeNumberRef,
    persistCanvasBatch,
    pushHistory,
    areNodesLocked,
    isGroupLocked,
    setToolbarLoadingKey,
}) => {
    // 获取当前选中的分组节点
    const getSelectedGroupNodes = useCallback(() => (
        nodesRef.current.filter((node) => selectedNodeIds.includes(node.id) && node.type === 'groupNode')
    ), [selectedNodeIds, nodesRef]);

    // 获取当前可参与编组的顶层节点
    const getSelectedGroupableNodes = useCallback(() => (
        nodesRef.current.filter((node) => selectedNodeIds.includes(node.id) && node.type !== 'groupNode' && !node.parentId)
    ), [selectedNodeIds, nodesRef]);

    // 判断当前选择是否满足“向已有分组追加节点”的条件
    const getSelectedAppendableGroup = useCallback(() => {
        const currentNodes = nodesRef.current;
        const selectedTopLevelNodes = currentNodes.filter((node) => (
            selectedNodeIds.includes(node.id) && !node.parentId
        ));
        const selectedGroupNodes = selectedTopLevelNodes.filter((node) => node.type === 'groupNode');
        const selectedLooseNodes = selectedTopLevelNodes.filter((node) => node.type !== 'groupNode');

        if (
            selectedGroupNodes.length !== 1
            || selectedLooseNodes.length < 1
            || selectedGroupNodes.length + selectedLooseNodes.length !== selectedTopLevelNodes.length
        ) {
            return null;
        }

        return {
            groupNode: selectedGroupNodes[0],
            looseNodes: selectedLooseNodes,
        };
    }, [selectedNodeIds, nodesRef]);

    // 将选中节点编组，或追加到已有分组
    const handleGroupSelection = useCallback(() => {
        const currentNodes = nodesRef.current;
        const currentEdges = edgesRef.current;
        const selectedTopLevelNodes = currentNodes.filter((node) => (
            selectedNodeIds.includes(node.id) && !node.parentId
        ));
        const groupableNodes = selectedTopLevelNodes.filter((node) => node.type !== 'groupNode');
        const appendableGroupSelection = getSelectedAppendableGroup();
        if (areNodesLocked(selectedTopLevelNodes.map((node) => node.id))) return false;

        if (appendableGroupSelection) {
            const { groupNode, looseNodes } = appendableGroupSelection;
            if (isGroupLocked(groupNode.id) || areNodesLocked(looseNodes.map((node) => node.id))) return false;
            const groupId = groupNode.id;
            const nodeMap = new Map(currentNodes.map((node) => [node.id, node]));
            const existingChildNodes = currentNodes.filter((node) => node.parentId === groupId);
            const mergedChildNodes = existingChildNodes.concat(looseNodes);
            const mergedStructureValidation = validateGroupFlowStructure(mergedChildNodes, currentEdges);

            if (!mergedStructureValidation.valid) {
                message.warning(mergedStructureValidation.message);
                return false;
            }

            const mergedChildIdSet = new Set(mergedChildNodes.map((node) => node.id));
            const bounds = mergedChildNodes.reduce((acc, node) => {
                const position = getNodeAbsolutePosition(node, nodeMap);
                const { width, height } = getNodeSize(node);

                return {
                    minX: Math.min(acc.minX, position.x),
                    minY: Math.min(acc.minY, position.y),
                    maxX: Math.max(acc.maxX, position.x + width),
                    maxY: Math.max(acc.maxY, position.y + height),
                };
            }, {
                minX: Number.POSITIVE_INFINITY,
                minY: Number.POSITIVE_INFINITY,
                maxX: Number.NEGATIVE_INFINITY,
                maxY: Number.NEGATIVE_INFINITY,
            });

            const nextGroupPosition = {
                x: bounds.minX - GROUP_PADDING_X,
                y: bounds.minY - GROUP_HEADER_HEIGHT,
            };
            const nextGroupWidth = Math.max(GROUP_MIN_WIDTH, bounds.maxX - bounds.minX + (GROUP_PADDING_X * 2));
            const nextGroupHeight = Math.max(GROUP_MIN_HEIGHT, bounds.maxY - bounds.minY + GROUP_HEADER_HEIGHT + GROUP_PADDING_BOTTOM);

            pushHistory();
            const nextNodes = [];

            currentNodes.forEach((node) => {
                if (node.id === groupId) {
                    nextNodes.push({
                        ...node,
                        selected: true,
                        position: nextGroupPosition,
                        width: nextGroupWidth,
                        height: nextGroupHeight,
                        measured: undefined,
                        style: {
                            ...(node.style || {}),
                            width: nextGroupWidth,
                            height: nextGroupHeight,
                            zIndex: 0,
                        },
                    });
                    return;
                }

                if (!mergedChildIdSet.has(node.id)) {
                    nextNodes.push({ ...node, selected: false });
                    return;
                }

                const absolutePosition = getNodeAbsolutePosition(node, nodeMap);
                nextNodes.push({
                    ...node,
                    parentId: groupId,
                    extent: 'parent',
                    selected: false,
                    position: {
                        x: absolutePosition.x - nextGroupPosition.x,
                        y: absolutePosition.y - nextGroupPosition.y,
                    },
                });
            });

            const groupNodeIndex = nextNodes.findIndex((node) => node.id === groupId);
            if (groupNodeIndex > -1) {
                const [targetGroupNode] = nextNodes.splice(groupNodeIndex, 1);
                const childInsertIndex = nextNodes.findIndex((node) => node.parentId === groupId);
                nextNodes.splice(childInsertIndex > -1 ? childInsertIndex : nextNodes.length, 0, targetGroupNode);
            }

            setNodes(nextNodes);
            setActiveGroupId(groupId);
            persistCanvasBatch({
                beforeNodes: currentNodes,
                beforeEdges: currentEdges,
                afterNodes: nextNodes,
                afterEdges: currentEdges,
            });
            return true;
        }

        if (groupableNodes.length < 2 || groupableNodes.length !== selectedTopLevelNodes.length) return false;
        const groupStructureValidation = validateGroupFlowStructure(groupableNodes, currentEdges);

        if (!groupStructureValidation.valid) {
            message.warning(groupStructureValidation.message);
            return false;
        }

        const nodeMap = new Map(currentNodes.map((node) => [node.id, node]));
        const bounds = groupableNodes.reduce((acc, node) => {
            const position = getNodeAbsolutePosition(node, nodeMap);
            const { width, height } = getNodeSize(node);

            return {
                minX: Math.min(acc.minX, position.x),
                minY: Math.min(acc.minY, position.y),
                maxX: Math.max(acc.maxX, position.x + width),
                maxY: Math.max(acc.maxY, position.y + height),
            };
        }, {
            minX: Number.POSITIVE_INFINITY,
            minY: Number.POSITIVE_INFINITY,
            maxX: Number.NEGATIVE_INFINITY,
            maxY: Number.NEGATIVE_INFINITY,
        });

        const groupId = createNodeId('group-node');
        const nextGroupLabel = `组${nextGroupNodeNumberRef.current}`;
        const groupPosition = {
            x: bounds.minX - GROUP_PADDING_X,
            y: bounds.minY - GROUP_HEADER_HEIGHT,
        };
        const groupWidth = Math.max(GROUP_MIN_WIDTH, bounds.maxX - bounds.minX + (GROUP_PADDING_X * 2));
        const groupHeight = Math.max(GROUP_MIN_HEIGHT, bounds.maxY - bounds.minY + GROUP_HEADER_HEIGHT + GROUP_PADDING_BOTTOM);

        pushHistory();
        const nextNodes = (() => {
            const selectedIds = new Set(groupableNodes.map((node) => node.id));
            const nextGroupNode = {
                id: groupId,
                type: 'groupNode',
                position: groupPosition,
                selected: true,
                style: {
                    width: groupWidth,
                    height: groupHeight,
                    zIndex: 0,
                },
                data: {
                    label: nextGroupLabel,
                },
            };
            const untouchedNodes = [];
            const groupedChildNodes = [];

            currentNodes.forEach((node) => {
                if (!selectedIds.has(node.id)) {
                    untouchedNodes.push({ ...node, selected: false });
                    return;
                }

                const absolutePosition = getNodeAbsolutePosition(node, nodeMap);
                groupedChildNodes.push({
                    ...node,
                    parentId: groupId,
                    extent: 'parent',
                    selected: false,
                    position: {
                        x: absolutePosition.x - groupPosition.x,
                        y: absolutePosition.y - groupPosition.y,
                    },
                });
            });

            return untouchedNodes.concat(nextGroupNode, groupedChildNodes);
        })();

        setNodes(nextNodes);
        setActiveGroupId(groupId);
        setNextGroupNodeNumber((prev) => prev + 1);
        persistCanvasBatch({
            beforeNodes: currentNodes,
            beforeEdges: currentEdges,
            afterNodes: nextNodes,
            afterEdges: currentEdges,
        });
        return true;
    }, [areNodesLocked, getSelectedAppendableGroup, isGroupLocked, persistCanvasBatch, pushHistory, selectedNodeIds, setNodes, nodesRef, edgesRef, setActiveGroupId, setNextGroupNodeNumber, nextGroupNodeNumberRef]);

    // 解散选中的分组并还原子节点位置
    const handleUngroupSelection = useCallback((groupIds = null) => {
        const selectedGroupIds = groupIds
            ? Array.from(new Set(groupIds))
            : getSelectedGroupNodes().map((node) => node.id);

        if (selectedGroupIds.length === 0) return false;
        if (selectedGroupIds.some((groupId) => isGroupLocked(groupId))) return false;

        const targetGroupIds = new Set(selectedGroupIds);
        const currentNodes = nodesRef.current;
        const currentEdges = edgesRef.current;
        const nodeMap = new Map(currentNodes.map((node) => [node.id, node]));

        if (activeGroupId && targetGroupIds.has(activeGroupId)) {
            setActiveGroupId(null);
        }
        pushHistory();
        const nextNodes = currentNodes
            .filter((node) => !targetGroupIds.has(node.id))
            .map((node) => {
                if (!node.parentId || !targetGroupIds.has(node.parentId)) {
                    return {
                        ...node,
                        selected: false,
                    };
                }

                const parentNode = nodeMap.get(node.parentId);
                return {
                    ...node,
                    selected: true,
                    position: {
                        x: node.position.x + (parentNode?.position.x ?? 0),
                        y: node.position.y + (parentNode?.position.y ?? 0),
                    },
                    parentId: undefined,
                    extent: undefined,
                    expandParent: undefined,
                };
            });

        setNodes(nextNodes);
        persistCanvasBatch({
            beforeNodes: currentNodes,
            beforeEdges: currentEdges,
            afterNodes: nextNodes,
            afterEdges: currentEdges,
        });
        return true;
    }, [activeGroupId, getSelectedGroupNodes, isGroupLocked, persistCanvasBatch, pushHistory, setNodes, nodesRef, edgesRef, setActiveGroupId]);

    // 打包下载当前分组内的结果图和素材图
    const handleBatchDownloadGroup = useCallback(async () => {
        if (!activeGroupId) return;
        setToolbarLoadingKey('download-group');

        const groupNodes = nodesRef.current.filter((node) => (
            node.id === activeGroupId || node.parentId === activeGroupId
        ));
        const resultItems = groupNodes
            .flatMap((node) => getNodeResultImageItems(node))
            .filter((item) => item?.url);
        const resultUrlSet = new Set(resultItems.map((item) => item.url));
        const sourceItems = groupNodes
            .flatMap((node) => getNodeSourceImageItems(node))
            .filter((item) => item?.url && !resultUrlSet.has(item.url));
        const downloadItems = resultItems.concat(sourceItems);
        const uniqueDownloadItems = Array.from(
            downloadItems.reduce((acc, item) => {
                const uniqueKey = `${item.category || 'source'}:${item.url}`;
                if (!acc.has(uniqueKey)) {
                    acc.set(uniqueKey, item);
                }
                return acc;
            }, new Map()).values(),
        );

        if (!uniqueDownloadItems.length) {
            message.warning('当前组合内暂无可下载图片');
            setToolbarLoadingKey('');
            return;
        }

        const groupNode = nodesRef.current.find((node) => node.id === activeGroupId);
        const zip = new JSZip();

        try {
            await Promise.all(uniqueDownloadItems.map(async (item, index) => {
                const response = await fetch(item.url);
                if (!response.ok) {
                    throw new Error('下载图片失败');
                }

                const blob = await response.blob();
                const fallbackName = getDownloadFileName(item.url, blob, `${item.nodeLabel || 'node'}-${index + 1}`);
                const extension = getExtensionFromBlob(blob, item.url);
                const baseName = fallbackName.replace(/\.[a-zA-Z0-9]+$/, '');
                const prefix = item.category === 'result' ? '结果图_' : '素材图_';
                const fileName = `${prefix}${baseName || `group-image-${index + 1}`}.${extension}`;
                zip.file(fileName, blob);
            }));

            const zipBlob = await zip.generateAsync({ type: 'blob' });
            const zipName = `${groupNode?.data?.label || '组合'}-${Date.now()}.zip`;
            saveAs(zipBlob, zipName);
        } catch (error) {
            console.error(error);
            message.error('批量下载失败');
        }
        setToolbarLoadingKey('');
    }, [activeGroupId, nodesRef, setToolbarLoadingKey]);

    // 打包下载选中节点内的结果图和素材图
    const handleBatchDownloadSelection = useCallback(async (nodeIds = []) => {
        const normalizedNodeIds = Array.from(new Set((nodeIds || []).filter(Boolean)));
        if (!normalizedNodeIds.length) return;
        setToolbarLoadingKey('download-selection');

        const selectedNodes = nodesRef.current.filter((node) => normalizedNodeIds.includes(node.id));
        const resultItems = selectedNodes
            .flatMap((node) => getNodeResultImageItems(node))
            .filter((item) => item?.url);
        const resultUrlSet = new Set(resultItems.map((item) => item.url));
        const sourceItems = selectedNodes
            .flatMap((node) => getNodeSourceImageItems(node))
            .filter((item) => item?.url && !resultUrlSet.has(item.url));
        const downloadItems = resultItems.concat(sourceItems);
        const uniqueDownloadItems = Array.from(
            downloadItems.reduce((acc, item) => {
                const uniqueKey = `${item.category || 'source'}:${item.url}`;
                if (!acc.has(uniqueKey)) {
                    acc.set(uniqueKey, item);
                }
                return acc;
            }, new Map()).values(),
        );

        if (!uniqueDownloadItems.length) {
            message.warning('当前框选范围内暂无可下载图片');
            setToolbarLoadingKey('');
            return;
        }

        const zip = new JSZip();

        try {
            await Promise.all(uniqueDownloadItems.map(async (item, index) => {
                const response = await fetch(item.url);
                if (!response.ok) {
                    throw new Error('下载图片失败');
                }

                const blob = await response.blob();
                const fallbackName = getDownloadFileName(item.url, blob, `${item.nodeLabel || 'node'}-${index + 1}`);
                const extension = getExtensionFromBlob(blob, item.url);
                const baseName = fallbackName.replace(/\.[a-zA-Z0-9]+$/, '');
                const prefix = item.category === 'result' ? '结果图_' : '素材图_';
                const fileName = `${prefix}${baseName || `selection-image-${index + 1}`}.${extension}`;
                zip.file(fileName, blob);
            }));

            const zipBlob = await zip.generateAsync({ type: 'blob' });
            saveAs(zipBlob, `框选结果-${Date.now()}.zip`);
        } catch (error) {
            console.error(error);
            message.error('批量下载失败');
        }
        setToolbarLoadingKey('');
    }, [nodesRef, setToolbarLoadingKey]);

    return {
        getSelectedGroupNodes,
        getSelectedGroupableNodes,
        getSelectedAppendableGroup,
        handleGroupSelection,
        handleUngroupSelection,
        handleBatchDownloadGroup,
        handleBatchDownloadSelection,
    };
};